import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { verificationAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const DocumentVerification = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [notes, setNotes] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async (status = null) => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const response = await verificationAPI.getPendingDocuments(params);
      setDocuments(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const statuses = [null, 'pending', 'approved', 'rejected'];
    fetchDocuments(statuses[newValue]);
  };

  const handleApprove = async () => {
    try {
      await verificationAPI.approveDocument(selectedDoc.id, notes);
      toast.success('Document approved successfully');
      setDialogOpen(false);
      const statuses = [null, 'pending', 'approved', 'rejected'];
      fetchDocuments(statuses[tabValue]);
    } catch (error) {
      toast.error('Failed to approve document');
    }
  };

  const handleReject = async () => {
    try {
      await verificationAPI.rejectDocument(selectedDoc.id, notes);
      toast.success('Document rejected successfully');
      setDialogOpen(false);
      const statuses = [null, 'pending', 'approved', 'rejected'];
      fetchDocuments(statuses[tabValue]);
    } catch (error) {
      toast.error('Failed to reject document');
    }
  };

  const openDialog = (doc, action) => {
    setSelectedDoc(doc);
    setActionType(action);
    setNotes('');
    setDialogOpen(true);
  };

  const getStatusCount = (status) => {
    if (status === 'all') return documents.length;
    return documents.filter(doc => doc.status === status).length;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getTabLabel = (index) => {
    const labels = ['All', 'Pending', 'Approved', 'Rejected'];
    return labels[index] || '';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Document Verification
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => {
            const statuses = [null, 'pending', 'approved', 'rejected'];
            fetchDocuments(statuses[tabValue]);
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab 
            label={
              <Badge badgeContent={getStatusCount('all')} color="primary">
                All
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatusCount('pending')} color="warning">
                Pending
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatusCount('approved')} color="success">
                Approved
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatusCount('rejected')} color="error">
                Rejected
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No {tabValue === 0 ? '' : getStatusLabel(['pending', 'approved', 'rejected'][tabValue - 1])} documents found
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {documents.map((doc) => (
            <Grid item xs={12} md={6} key={doc.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box>
                      <Typography variant="h6">
                        {doc.first_name} {doc.last_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        User ID: {doc.user_id} | Phone: {doc.phone}
                      </Typography>
                    </Box>
                    <Chip 
                      label={doc.document_type_name || 'Document'} 
                      color="primary" 
                      size="small"
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Submitted: {moment(doc.created_at).fromNow()}
                    </Typography>
                    <Chip 
                      label={getStatusLabel(doc.status)}
                      color={getStatusColor(doc.status)}
                      size="small"
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {doc.front_image_url && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>Front Image</Typography>
                        <img 
                          src={doc.front_image_url} 
                          alt="Front" 
                          style={{ width: '100%', borderRadius: 8, cursor: 'pointer' }}
                          onClick={() => window.open(doc.front_image_url, '_blank')}
                        />
                      </Grid>
                    )}
                    {doc.back_image_url && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>Back Image</Typography>
                        <img 
                          src={doc.back_image_url} 
                          alt="Back" 
                          style={{ width: '100%', borderRadius: 8, cursor: 'pointer' }}
                          onClick={() => window.open(doc.back_image_url, '_blank')}
                        />
                      </Grid>
                    )}
                    {doc.face_photo_url && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Face Photo</Typography>
                        <img 
                          src={doc.face_photo_url} 
                          alt="Face" 
                          style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8, cursor: 'pointer' }}
                          onClick={() => window.open(doc.face_photo_url, '_blank')}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {doc.admin_notes && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                      Notes: {doc.admin_notes}
                    </Typography>
                  )}

                  {doc.status === 'pending' && (
                    <Box display="flex" gap={2} sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ApproveIcon />}
                        fullWidth
                        onClick={() => openDialog(doc, 'approve')}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<RejectIcon />}
                        fullWidth
                        onClick={() => openDialog(doc, 'reject')}
                      >
                        Reject
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Document' : 'Reject Document'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Admin Notes"
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={actionType === 'approve' ? 'Add any notes (optional)' : 'Provide reason for rejection'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={actionType === 'approve' ? handleApprove : handleReject}
            color={actionType === 'approve' ? 'success' : 'error'}
            variant="contained"
          >
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentVerification;